/**
 * Company events as chart markers: which bar each one lands on, and what its
 * popup says.
 *
 * Pure and here rather than in the view, for `chart-window.ts`'s reason: the
 * chart itself is out of jsdom's reach, so the only testable form of this is
 * arithmetic over numbers somebody else measured. What the view keeps is the
 * fetch and the hover.
 *
 * ## An event is dated, a bar is an index
 *
 * The X axis is a `DiscontinuousDateAxis`: it holds trading days and skips the
 * rest. An announcement lands on whatever day the company chose, which is
 * routinely a Saturday for a Friday-evening release and always a holiday
 * somewhere — so placing a marker at the raw timestamp puts it in a gap the axis
 * does not draw, and it renders somewhere between two unrelated bars.
 *
 * Every event is therefore **snapped to a loaded bar** before it is drawn, and
 * an event outside the loaded range is dropped rather than clamped. Clamping
 * would pile every old announcement onto the first bar, which reads as a company
 * that reported eleven times that morning.
 */

import { signedLogLabel, timeAtOffset, toSignedLog, type EstimateLine, type EventMarker } from "@qufolio/chart-engine";
import type { EarningsQuarter, SymbolFundamentals } from "../api/symbols";
import { alreadyReported, quarterColumns, quarterEstimate, reportedGrowth, reportedRevenue, yoyNote } from "./fin-quarters";

/** How the marker is labelled. One character, so a dense chart stays readable. */
export const EARNINGS_GLYPH = "E";
export const NEWS_GLYPH = "N";

/** `id` prefixes, so a hover can tell what it is over without a second lookup. */
export const EARNINGS_PREFIX = "earnings:";
export const NEWS_PREFIX = "news:";
export const NEXT_EARNINGS_PREFIX = "earnings-next:";

/**
 * Midnight UTC for a `YYYY-MM-DD`, in unix **seconds**, or `NaN`.
 *
 * **Seconds, not milliseconds**, because that is the unit the X axis carries —
 * `chart-window.ts` says so and `xRef` holds them. `Date.parse` returns
 * milliseconds, and the two differ by a factor of a thousand, so a marker built
 * in milliseconds lands roughly fifty thousand years past the last bar. Every
 * one is then dropped by `barTimeFor` as out of range, and the chart shows
 * nothing at all — no error, because being off the loaded window is a legitimate
 * answer that happens to look identical to the data never having arrived.
 *
 * **UTC, not local.** `new Date("2025-04-30")` is already UTC midnight, while
 * `new Date(2025, 3, 30)` is local — and west of Greenwich the two are a day
 * apart, which snaps the marker to the previous session. The same off-by-one
 * `chart-window.ts` records for a date typed into the toolbar.
 */
export function dayStart(date: string): number {
  return Date.parse(`${date.slice(0, 10)}T00:00:00Z`) / 1000;
}

/**
 * The loaded bar an event belongs to, or `undefined` if it is off the ends.
 *
 * **At or after, not nearest** — and that is why this is not
 * `indexAtTime`. That function is deliberately *nearest*, which is right for
 * what it serves: a drawing is pinned to the bar it was drawn on, and after a
 * resample the nearest bar is the same place on the chart. An announcement is a
 * different question. A company that reports on a Saturday reported *after
 * Friday's close*, and the session that answers it is Monday — nearest would put
 * the marker back on Friday, a bar whose close nobody had the news for.
 *
 * A release on a trading day lands on its own bar and needs no adjusting, which
 * is the common case and the one this must not disturb.
 */
export function barTimeFor(barTimes: readonly number[], date: string): number | undefined {
  const time = dayStart(date);
  if (!Number.isFinite(time) || barTimes.length === 0) return undefined;
  // Off either end is reported rather than clamped, for `annotation-x.ts`'
  // reason: clamping piles every announcement older than the window onto the
  // first bar, which reads as a company that reported eleven times that morning.
  if (time < barTimes[0]! || time > barTimes[barTimes.length - 1]!) return undefined;

  let lo = 0;
  let hi = barTimes.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (barTimes[mid]! < time) lo = mid + 1;
    else hi = mid;
  }
  return barTimes[lo];
}

/**
 * What colours an earnings marker.
 *
 * The **surprise**, and only the surprise — a quarter with no estimate to be
 * judged against is `neutral` rather than being coloured by something else that
 * happens to be at hand. Two different meanings behind one colour is worse than
 * a grey marker, because nothing on screen says which one you are looking at.
 *
 * Exactly zero is a beat of nothing, so it is neutral too. `> 0` rather than
 * `>= 0`.
 */
export function earningsTone(quarter: EarningsQuarter): EventMarker["tone"] {
  const surprise = quarter.epsSurprise;
  if (surprise === null || surprise === undefined || !Number.isFinite(surprise)) {
    return "neutral";
  }
  if (surprise > 0) return "positive";
  if (surprise < 0) return "negative";
  return "neutral";
}

/**
 * One marker per reported quarter, snapped to the bars that are loaded.
 *
 * Quarters that fall outside the window are simply absent — scrolling back
 * brings them in, because the caller re-runs this whenever the loaded bars
 * change.
 *
 * **Deduplicated by bar, not by date.** Two announcements can snap to the same
 * session — a Friday-night release and a Monday-morning one both land on
 * Monday when the market was shut between them — and two glyphs at one x are
 * one illegible smudge. The later quarter wins, being the one a reader on that
 * bar is looking at.
 */
export function earningsMarkers(
  quarters: readonly EarningsQuarter[],
  barTimes: readonly number[],
): EventMarker[] {
  if (barTimes.length === 0) return [];
  // Over **every** quarter, not only the loaded window's: the oldest `E` on
  // screen still has a year-ago report, just off the left edge.
  const growth = reportedGrowth(quarters);
  const byBar = new Map<number, EventMarker>();
  // A duplicate report (under 30 days after the previous) keeps its `E` — the
  // popup still has its numbers — but `reportedGrowth` skips it, so it carries
  // no note and nothing is measured against it.
  for (const quarter of quarters) {
    if (!quarter.date) continue;
    const time = barTimeFor(barTimes, quarter.date);
    if (time === undefined) continue;
    // **QoQ until the first YoY exists, marked `Q`** (owner, 2026-10-07): a
    // newly listed company has no report a year back, and a bare number there
    // would read as a yearly change. The prefix says it is quarter on quarter,
    // which swings with the season. Chart only; the screener's EPS YoY column
    // stays YoY.
    const reading = growth.get(quarter.date);
    const quarterly = (reading?.yoy ?? null) === null && (reading?.qoq ?? null) !== null;
    const yoy = quarterly ? reading!.qoq : reading?.yoy;
    const base = yoyNote(yoy);
    const note = base && quarterly ? `Q${base}` : base;
    byBar.set(time, {
      id: `${EARNINGS_PREFIX}${quarter.date}`,
      time,
      label: EARNINGS_GLYPH,
      tone: earningsTone(quarter),
      title: "Earnings & Revenue",
      // Reported-EPS YoY above the glyph (owner, 2026-10-07). The glyph's own
      // colour stays the surprise, so the note is muted unless it is negative.
      ...(note ? { note, noteTone: (yoy ?? 0) < 0 ? ("negative" as const) : ("neutral" as const) } : {}),
    });
  }
  return [...byBar.values()].sort((a, b) => a.time - b.time);
}

/**
 * The news marker, on the last loaded bar.
 *
 * **One marker, not one per article.** The feed carries no per-article ticker
 * timestamps we can trust to a session — `/sym/news` is "the recent ones for
 * this symbol" — so spreading them across bars would be inventing a precision
 * the data does not have. One glyph on the newest bar says *there is news about
 * this symbol* and the popup says what, which is the honest shape of what is
 * known.
 *
 * Neutral, always: a headline has no sign. Colouring it by sentiment would be
 * asserting a reading of the news that nothing here computes.
 */
export function newsMarker(barTimes: readonly number[], count: number): EventMarker | null {
  if (barTimes.length === 0 || count <= 0) return null;
  const time = barTimes[barTimes.length - 1]!;
  return {
    id: `${NEWS_PREFIX}${time}`,
    time,
    label: NEWS_GLYPH,
    tone: "neutral",
    title: "News",
  };
}

/**
 * The report not yet made: its date, the consensus, and the consensus against
 * the reported quarters — what the future `E` and its popup say (owner,
 * 2026-10-28). The YoY/QoQ are Fin St's estimate column's, from the same
 * `quarterEstimate`, so the chart and the table cannot disagree.
 */
export interface UpcomingEarnings {
  quarter: EarningsQuarter;
  growth: { eps: Growth; revenue: Growth };
}

/** The next report from a fundamentals document, or `null` where none is published. */
export function upcomingEarnings(document: SymbolFundamentals | null | undefined): UpcomingEarnings | null {
  if (!document) return null;
  const metrics: Record<string, unknown> = { ...document.depth, ...document.metrics };
  const date = typeof metrics.nextEarningsDate === "string" ? metrics.nextEarningsDate.slice(0, 10) : null;
  if (!date || !Number.isFinite(dayStart(date))) return null;
  // The report just made, still named as the next one — see `alreadyReported`.
  if (alreadyReported(document.earnings ?? [], date)) return null;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  // Placed and compared only where the estimate is the next quarter's — see
  // `quarterEstimate`; otherwise the date and the consensus still stand.
  const est = quarterEstimate(quarterColumns(document.periods ?? [], document.earnings ?? []), metrics);
  const read = (id: string) => (est ? num(est[id]) : null);
  return {
    quarter: {
      date,
      periodEnd: est?.periodEnd ?? null,
      epsActual: null,
      epsEstimated: num(metrics.epsEstimate),
      revenueEstimated: num(metrics.revenueEstimate),
    },
    growth: {
      eps: { yoy: read("epsReportedYoY"), qoq: read("epsReportedQoQ") },
      revenue: { yoy: read("revenueGrowth"), qoq: read("revenueQoQ") },
    },
  };
}

/** Weekdays after `from` up to and including `to`, both unix seconds at midnight UTC. */
function weekdaysBetween(from: number, to: number): number {
  let count = 0;
  for (let t = from + 86_400; t <= to; t += 86_400) {
    const day = new Date(t * 1000).getUTCDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
}

/**
 * The future `E`: yellow (`pending`), on the date of the next report, and
 * `pinned` so the chart holds it at the right edge with a `›` while that date is
 * further out than the view (owner, 2026-10-28).
 *
 * `bars` are the real bars; `axis` is everything the X axis holds, projection
 * included. A date inside the projection snaps to its projected bar. Past it,
 * the date is counted in **trading days** and turned into bars at this chart's
 * spacing (one for daily, five for weekly), then into the time the axis
 * extrapolates to for that many bars — so a Friday-to-Monday step cannot put it
 * three bars out per day. Intraday charts get none: a report a month away is
 * thousands of bars off.
 *
 * `null` on the report day itself and after: once the date has a bar, the
 * reported `E` is the one that belongs there.
 */
export function upcomingMarker(
  upcoming: UpcomingEarnings | null,
  bars: readonly number[],
  axis: readonly number[] = bars,
): EventMarker | null {
  if (!upcoming || bars.length < 2) return null;
  const date = upcoming.quarter.date;
  const when = dayStart(date);
  const last = bars[bars.length - 1]!;
  const prev = bars[bars.length - 2]!;
  if (!Number.isFinite(when) || dayStart(new Date(last * 1000).toISOString()) >= when) return null;
  if (last - prev < 86_400) return null;

  let time = axis.length > bars.length ? barTimeFor(axis, date) : undefined;
  if (time === undefined) {
    const end = axis[axis.length - 1]!;
    const perBar = Math.max(1, weekdaysBetween(dayStart(new Date(prev * 1000).toISOString()), dayStart(new Date(last * 1000).toISOString())));
    const ahead = Math.max(1, Math.round(weekdaysBetween(dayStart(new Date(end * 1000).toISOString()), when) / perBar));
    time = timeAtOffset(axis, axis.length - 1 + ahead);
  }
  if (time === undefined) return null;

  const yoy = upcoming.growth.eps.yoy;
  const note = yoyNote(yoy);
  return {
    id: `${NEXT_EARNINGS_PREFIX}${date}`,
    time,
    label: EARNINGS_GLYPH,
    tone: "pending",
    pinned: true,
    title: "Next earnings",
    ...(note ? { note, noteTone: (yoy ?? 0) < 0 ? ("negative" as const) : ("neutral" as const) } : {}),
  };
}

/** One reading's change, year on year and quarter on quarter. */
export interface Growth {
  yoy: number | null;
  qoq: number | null;
}

/**
 * The popup's YoY and QoQ for an earnings marker: EPS (the number its note
 * shows) and revenue (owner, 2026-10-08), by the same rules as the screener.
 */
export function growthFor(
  quarters: readonly EarningsQuarter[],
  markerId: string,
): { eps?: Growth; revenue?: Growth } | undefined {
  if (!markerId.startsWith(EARNINGS_PREFIX)) return undefined;
  const date = markerId.slice(EARNINGS_PREFIX.length);
  return {
    eps: reportedGrowth(quarters).get(date),
    revenue: reportedGrowth(quarters, reportedRevenue).get(date),
  };
}

/** The quarter a marker id names, for the popup. */
export function quarterFor(
  quarters: readonly EarningsQuarter[],
  markerId: string,
): EarningsQuarter | undefined {
  if (!markerId.startsWith(EARNINGS_PREFIX)) return undefined;
  const date = markerId.slice(EARNINGS_PREFIX.length);
  return quarters.find((quarter) => quarter.date === date);
}

/**
 * The reported-growth indicators an estimate line is drawn for, and which of
 * `UpcomingEarnings.growth` each reads (owner, 2026-10-29).
 */
export const ESTIMATED_SERIES: Readonly<Record<string, readonly ["eps" | "revenue", "yoy" | "qoq"]>> = {
  epsYoYReported: ["eps", "yoy"],
  epsQoQReported: ["eps", "qoq"],
  revYoYReported: ["revenue", "yoy"],
  revQoQReported: ["revenue", "qoq"],
};

/**
 * The yellow estimate lines: on each EPS / revenue YoY or QoQ plot, what that
 * reading would be if the next report meets its consensus — from the future
 * `E`'s time (`upcomingMarker`) onward. The numbers are the popup's and Fin St's
 * estimate column's, so the three cannot disagree; none where they have none.
 *
 * Only the plain series: a smoothed copy (`epsYoYReported~sma20`) is an average
 * the estimate is not a point of.
 */
export function estimateLines(
  upcoming: UpcomingEarnings | null,
  at: number | undefined,
  plots: readonly { id: string; outputAlias?: string }[],
): EstimateLine[] {
  if (!upcoming || at === undefined) return [];
  const out: EstimateLine[] = [];
  for (const plot of plots) {
    const reads = plot.outputAlias ? ESTIMATED_SERIES[plot.outputAlias] : undefined;
    if (!reads) continue;
    const value = upcoming.growth[reads[0]][reads[1]];
    if (value === null || !Number.isFinite(value)) continue;
    out.push({ plotId: plot.id, time: at, value, label: `Est ${signedLogLabel(toSignedLog(value))}` });
  }
  return out;
}
